'use client'
import { useEffect, useRef, Suspense } from 'react'
import posthog from 'posthog-js'
import { usePathname } from 'next/navigation'
import { getCurrentTraceId, startNewTrace } from '@/lib/trace-context'

// Tracks whether a TracedLink click has already started and registered a trace
// for the upcoming navigation. On direct page loads (bookmark, external link,
// hard refresh) no TracedLink click fires, so we start a fresh trace and
// register it as a PostHog super-property so the $pageview and all subsequent
// API calls share the same traceId.
let traceRegisteredByLink = false

/** Called by TracedLink after it fires startNewTrace() + posthog.register(). */
export function markTracedLinkFired() {
  traceRegisteredByLink = true
}

function PostHogPageView() {
  const pathname = usePathname()
  const lastPathname = useRef<string | null>(null)

  useEffect(() => {
    // Only fire $pageview when the actual page path changes, not on query param updates
    if (pathname !== lastPathname.current) {
      lastPathname.current = pathname

      // If a TracedLink click already started and registered a trace, reuse it.
      // Otherwise (direct load / hard refresh / external link) start a new trace
      // and register it so the $pageview and API calls share the same traceId.
      let traceId = getCurrentTraceId()
      if (!traceRegisteredByLink) {
        traceId = startNewTrace()
        try {
          posthog.register({ traceId })
        } catch {
          // non-fatal
        }
      }

      // Reset for the next navigation — TracedLink will set it again on click.
      traceRegisteredByLink = false

      posthog.capture('$pageview', { $current_url: window.location.href, traceId })
    }
  }, [pathname])

  return null
}

export function SuspendedPostHogPageView() {
  return (
    <Suspense fallback={null}>
      <PostHogPageView />
    </Suspense>
  )
}
